<template>
  <v-card>
    <v-card-title> Havi összesítōk </v-card-title>

    <v-card-text>
      <!-- Filters -->
      <v-row class="mb-4" align="center" dense>
        <!-- Company selector -->
        <v-col cols="12" sm="3">
          <v-select
            v-model="selectedCompanyId"
            :items="companies"
            item-title="name"
            item-value="id"
            label="Cég"
          />
        </v-col>

        <v-col cols="12" sm="3">
          <v-select
            v-model="selectedCostCenterId"
            :items="costCenters"
            item-title="name"
            item-value="id"
            label="Költséghely"
          />
        </v-col>

        <v-col cols="12" sm="2">
          <v-select v-model="year" :items="years" label="Év" />
        </v-col>

        <v-col cols="12" sm="2">
          <v-select v-model="month" :items="months" label="Hónap" />
        </v-col>

        <v-col cols="12" sm="3">
          <v-btn
            color="primary"
            @click="loadReport"
            :loading="reportLoading"
            :disabled="
              reportLoading || !selectedCompanyId || !selectedCostCenterId
            "
            >Jelentés betöltése</v-btn
          >
        </v-col>
        <v-col cols="12" sm="3">
          <v-btn
            color="primary"
            @click="createSheet"
            :loading="sheetLoading"
            :disabled="
              sheetLoading || !selectedCompanyId || !selectedCostCenterId
            "
            >Táblázat létrehozása</v-btn
          >
        </v-col>
      </v-row>

      <v-alert v-if="sheetUrl" type="success" variant="tonal" class="mb-4">
        A táblázat elkészült:
        <a :href="sheetUrl" target="_blank">{{ sheetUrl }}</a>
      </v-alert>

      <v-alert
        v-if="reportLoaded && rows.length === 0"
        type="info"
        variant="tonal"
        class="mb-4"
      >
        Nincs adat a kiválasztott időszakra.
      </v-alert>

      <!-- Table -->
      <v-table>
        <thead>
          <tr>
            <th>Szállás neve</th>
            <th>Szállás típusa</th>
            <th>Nettó egységár/fő/éj</th>

            <th v-for="day in daysInMonth" :key="day" class="day-column">
              {{ day }}
            </th>

            <th class="totals-column">Összes vendégéj</th>
            <th class="totals-column">Nettó összesen</th>
            <th class="totals-column">Bejövő bruttó ár/fő/éj</th>
            <th class="totals-column">Árrés nettó/fő/éj</th>
            <th class="totals-column">Nettó árrés összesen</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="row in rows"
            :key="
              row.accommodation +
              row.product +
              row.retailPrice +
              row.grossPricePerPersonPerNight
            "
          >
            <td>{{ row.accommodation }}</td>
            <td>{{ row.product }}</td>
            <td>{{ row.retailPrice }}</td>

            <td v-for="day in daysInMonth" :key="day" class="day-column">
              {{ row.days[day] || 0 }}
            </td>

            <td class="totals-column">{{ row.totalNights }}</td>
            <td class="totals-column">{{ row.totalPrice }}</td>
            <td class="totals-column">
              {{ row.grossPricePerPersonPerNight ?? "" }}
            </td>
            <td class="totals-column">
              {{ row.marginPerPersonPerNight ?? "" }}
            </td>
            <td class="totals-column">{{ row.totalMargin ?? "" }}</td>
          </tr>
        </tbody>

        <tfoot>
          <tr>
            <td colspan="3"><strong>Végösszeg</strong></td>

            <td v-for="day in daysInMonth" :key="day" class="day-column">
              {{ sumColumn(day) }}
            </td>

            <td class="totals-column">
              <strong>{{ grandTotalNights }}</strong>
            </td>

            <td class="totals-column">
              <strong>{{ grandTotalPrice }}</strong>
            </td>

            <td class="totals-column"></td>
            <td class="totals-column"></td>
            <td class="totals-column">
              <strong>{{ grandTotalMargin }}</strong>
            </td>
          </tr>
        </tfoot>
      </v-table>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import axiosInstance from "@/api/axiosInstance";
import { CompanyService } from "@/services/CompanyService";
import { CostCenterService } from "@/services/CostCenterService";

/* ---------------- STATE ---------------- */
const companyService = new CompanyService("/admin/companies");
const costCenterService = new CostCenterService("/admin/companies");
const rows = ref([]);
const sheetUrl = ref("");
const sheetLoading = ref(false);
const reportLoading = ref(false);
const reportLoaded = ref(false);

const selectedCompanyId = ref(null);
const companies = ref([]);
const selectedCostCenterId = ref(null);
const costCenters = ref([]);

const year = ref(new Date().getFullYear());
const month = ref(new Date().getMonth() + 1);

const daysInMonth = ref(31);

const years = Array.from(
  { length: 10 },
  (_, i) => new Date().getFullYear() - 5 + i
);
const months = Array.from({ length: 12 }, (_, i) => i + 1);

/* ---------------- COMPUTED ---------------- */

const formattedMonth = computed(() => {
  return `${year.value}-${String(month.value).padStart(2, "0")}`;
});

const grandTotalNights = computed(() =>
  rows.value.reduce((sum, row) => sum + (row.totalNights || 0), 0)
);

const grandTotalPrice = computed(() =>
  rows.value.reduce((sum, row) => sum + (row.totalPrice || 0), 0)
);

const grandTotalMargin = computed(() =>
  rows.value.reduce((sum, row) => sum + (row.totalMargin || 0), 0)
);

/* ---------------- METHODS ---------------- */

const fetchCompanies = async () => {
  try {
    const res = await companyService.getAll();

    companies.value = res;
  } catch (error) {
    console.error("Failed to fetch companies:", error);
  }
};

const fetchCostCenters = async () => {
  try {
    const data = await costCenterService.adminGetAllForCompany(
      selectedCompanyId.value
    );
    costCenters.value = data;
  } catch (error) {
    console.error("Failed to fetch cost centers", error);
  }
};

const loadReport = async () => {
  if (!selectedCompanyId.value || !selectedCostCenterId.value) return;

  reportLoading.value = true;
  try {
    const res = await axiosInstance.get(
      `/api/v1/admin/companies/${selectedCompanyId.value}/cost-centers/${selectedCostCenterId.value}/booking-summaries/monthly`,
      {
        params: {
          month: formattedMonth.value,
        },
      }
    );

    rows.value = res.data;
    reportLoaded.value = true;

    daysInMonth.value = new Date(year.value, month.value, 0).getDate();
  } catch (error) {
    console.error("Failed to load report:", error);
  } finally {
    reportLoading.value = false;
  }
};

const createSheet = async () => {
  if (!selectedCompanyId.value || !selectedCostCenterId.value) return;
  sheetLoading.value = true;
  sheetUrl.value = "";
  try {
    const res = await axiosInstance.get(
      `/api/v1/admin/companies/${selectedCompanyId.value}/cost-centers/${selectedCostCenterId.value}/booking-summaries/monthly-sheet`,
      { params: { month: formattedMonth.value } }
    );
    sheetUrl.value = res.data;
  } catch (error) {
    console.error("Failed to export report:", error);
  } finally {
    sheetLoading.value = false;
  }
};

watch(selectedCompanyId, async (newCompanyId) => {
  if (!newCompanyId) return;

  selectedCostCenterId.value = null;
  costCenters.value = [];

  await fetchCostCenters(newCompanyId);
});
const sumColumn = (day) => {
  return rows.value.reduce((sum, row) => sum + (row.days[day] || 0), 0);
};

/* ---------------- LIFECYCLE ---------------- */

onMounted(() => {
  fetchCompanies();
});
</script>

<style scoped>
.day-column {
  border-right: 2px solid #ccc;
  text-align: center;
}

.totals-column {
  font-weight: bold;
  text-align: center;
}
</style>
